import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, FileText, UtensilsCrossed, TrendingUp, Droplets, Moon, Activity, Smile, Calendar, Scale, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ProgressChart from '../components/ProgressChart';
import { formatDateBR, formatDateFullBR, formatDateLongBR } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPacienteDetalhes = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const [showMealPlanForm, setShowMealPlanForm] = useState(false);
  const [activeTab, setActiveTab] = useState('resumo'); // resumo, historico, medidas
  
  const [prescriptionData, setPrescriptionData] = useState({
    medicamento: '',
    dosagem: '',
    horarios: '',
    duracao_dias: '',
    observacoes: '',
    pdf_receita: ''
  });
  const [pdfFileName, setPdfFileName] = useState('');

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O PDF deve ter no máximo 10MB');
        return;
      }
      if (file.type !== 'application/pdf') {
        toast.error('Por favor, selecione um arquivo PDF');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPrescriptionData({ ...prescriptionData, pdf_receita: reader.result });
        setPdfFileName(file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const [mealPlanData, setMealPlanData] = useState({
    orientacoes: '',
    refeicoes: [
      { horario: '08:00', nome: 'Café da Manhã', alimentos: [], calorias: '' },
      { horario: '12:00', nome: 'Almoço', alimentos: [], calorias: '' },
      { horario: '15:00', nome: 'Lanche', alimentos: [], calorias: '' },
      { horario: '19:00', nome: 'Jantar', alimentos: [], calorias: '' }
    ]
  });

  useEffect(() => {
    fetchPatientData();
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/patients/${patientId}`);
      setPatientData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do paciente');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrescription = async (e) => {
    e.preventDefault();
    try {
      const data = {
        patient_id: patientId,
        medicamento: prescriptionData.medicamento,
        dosagem: prescriptionData.dosagem,
        horarios: prescriptionData.horarios.split(',').map(h => h.trim()),
        duracao_dias: parseInt(prescriptionData.duracao_dias),
        observacoes: prescriptionData.observacoes,
        pdf_receita: prescriptionData.pdf_receita || null
      };
      await axios.post(`${API_URL}/api/prescriptions`, data);
      toast.success('Prescrição criada com sucesso!');
      setShowPrescriptionForm(false);
      setPrescriptionData({
        medicamento: '',
        dosagem: '',
        horarios: '',
        duracao_dias: '',
        observacoes: '',
        pdf_receita: ''
      });
      setPdfFileName('');
      fetchPatientData();
    } catch (error) {
      console.error('Erro ao criar prescrição:', error);
      toast.error('Erro ao criar prescrição');
    }
  };

  const handleCreateMealPlan = async (e) => {
    e.preventDefault();
    try {
      const data = {
        patient_id: patientId,
        orientacoes: mealPlanData.orientacoes,
        refeicoes: mealPlanData.refeicoes.map(r => ({
          horario: r.horario,
          nome: r.nome,
          alimentos: r.alimentos,
          calorias: r.calorias ? parseInt(r.calorias) : null
        }))
      };
      await axios.post(`${API_URL}/api/meal-plans`, data);
      toast.success('Plano alimentar criado com sucesso!');
      setShowMealPlanForm(false);
      fetchPatientData();
    } catch (error) {
      console.error('Erro ao criar plano:', error);
      toast.error('Erro ao criar plano alimentar');
    }
  };

  const updateMealAlimentos = (index, value) => {
    const newRefeicoes = [...mealPlanData.refeicoes];
    newRefeicoes[index].alimentos = value.split('\n').filter(a => a.trim());
    setMealPlanData({ ...mealPlanData, refeicoes: newRefeicoes });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  if (!patientData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Paciente não encontrado</p>
        </div>
      </Layout>
    );
  }

  const pesoData = patientData.recent_progress
    .filter(p => p.peso)
    .map(p => ({ data: p.data.split('-').slice(1).join('/'), peso: p.peso }))
    .reverse();

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-button"
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {patientData.patient.nome}
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {patientData.patient.email}
            </p>
          </div>
        </div>

        {/* Patient Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Peso Inicial</p>
            <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {patientData.patient.peso_inicial || '--'} kg
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Meta</p>
            <p className="text-3xl font-bold text-[#B8977A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {patientData.patient.peso_meta || '--'} kg
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <p className="text-sm text-muted-foreground mb-2">Altura</p>
            <p className="text-3xl font-bold text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {patientData.patient.altura || '--'} m
            </p>
          </div>
        </div>

        {/* Tabs for different views */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setActiveTab('resumo')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'resumo'
                ? 'bg-[#0F2B4C] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <TrendingUp className="inline mr-2" size={18} />
            Resumo
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'historico'
                ? 'bg-[#B8977A] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Calendar className="inline mr-2" size={18} />
            Histórico Diário ({patientData.recent_progress?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('medidas')}
            className={`px-6 py-3 rounded-full font-semibold transition-all ${
              activeTab === 'medidas'
                ? 'bg-[#B8977A] text-white shadow-lg'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Ruler className="inline mr-2" size={18} />
            Medidas ({patientData.measurements?.length || 0})
          </button>
        </div>

        {/* Daily History Tab */}
        {activeTab === 'historico' && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Calendar className="text-[#B8977A]" size={24} />
              Histórico Diário do Paciente
            </h3>
            {patientData.recent_progress && patientData.recent_progress.length > 0 ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {patientData.recent_progress.map((entry, index) => (
                  <div key={entry.id || index} className="bg-muted rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-[#B8977A] text-lg">
                        {formatDateFullBR(entry.data)}
                      </span>
                      {entry.peso && (
                        <span className="px-4 py-1 bg-[#0F2B4C]/10 text-[#0F2B4C] rounded-full font-bold">
                          {entry.peso} kg
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {entry.humor && (
                        <div className="flex items-center gap-2 bg-white/50 rounded-lg p-2">
                          <Smile className="text-yellow-500" size={18} />
                          <div>
                            <p className="text-xs text-muted-foreground">Humor</p>
                            <p className="font-medium capitalize">{entry.humor}</p>
                          </div>
                        </div>
                      )}
                      {entry.agua_ml && (
                        <div className="flex items-center gap-2 bg-white/50 rounded-lg p-2">
                          <Droplets className="text-blue-500" size={18} />
                          <div>
                            <p className="text-xs text-muted-foreground">Água</p>
                            <p className="font-medium">{entry.agua_ml} ml</p>
                          </div>
                        </div>
                      )}
                      {entry.horas_sono && (
                        <div className="flex items-center gap-2 bg-white/50 rounded-lg p-2">
                          <Moon className="text-indigo-500" size={18} />
                          <div>
                            <p className="text-xs text-muted-foreground">Sono</p>
                            <p className="font-medium">{entry.horas_sono}h</p>
                          </div>
                        </div>
                      )}
                      {entry.atividade_fisica && (
                        <div className="flex items-center gap-2 bg-white/50 rounded-lg p-2">
                          <Activity className="text-green-500" size={18} />
                          <div>
                            <p className="text-xs text-muted-foreground">Atividade</p>
                            <p className="font-medium text-sm">{entry.atividade_fisica}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="mx-auto text-muted-foreground mb-4" size={48} />
                <p className="text-muted-foreground">Nenhum registro diário encontrado</p>
                <p className="text-sm text-muted-foreground mt-2">Os registros aparecerão aqui quando o paciente adicionar</p>
              </div>
            )}
          </div>
        )}

        {/* Measurements Tab */}
        {activeTab === 'medidas' && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Ruler className="text-[#B8977A]" size={24} />
              Medidas Corporais
            </h3>
            {patientData.measurements && patientData.measurements.length > 0 ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {patientData.measurements.map((entry, index) => (
                  <div key={entry.id || index} className="bg-muted rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-[#B8977A]">
                        {formatDateLongBR(entry.data)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {entry.cintura && (
                        <div className="bg-white/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Cintura</p>
                          <p className="font-bold text-lg text-highlight">{entry.cintura} cm</p>
                        </div>
                      )}
                      {entry.quadril && (
                        <div className="bg-white/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Quadril</p>
                          <p className="font-bold text-lg text-highlight">{entry.quadril} cm</p>
                        </div>
                      )}
                      {entry.peito && (
                        <div className="bg-white/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Peito</p>
                          <p className="font-bold text-lg text-highlight">{entry.peito} cm</p>
                        </div>
                      )}
                      {entry.braco && (
                        <div className="bg-white/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Braço</p>
                          <p className="font-bold text-lg text-highlight">{entry.braco} cm</p>
                        </div>
                      )}
                      {entry.perna && (
                        <div className="bg-white/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Perna</p>
                          <p className="font-bold text-lg text-highlight">{entry.perna} cm</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Ruler className="mx-auto text-muted-foreground mb-4" size={48} />
                <p className="text-muted-foreground">Nenhuma medida registrada</p>
                <p className="text-sm text-muted-foreground mt-2">As medidas aparecerão aqui quando o paciente adicionar</p>
              </div>
            )}
          </div>
        )}

        {/* Summary Tab Content */}
        {activeTab === 'resumo' && (
          <>
            {/* Progress Chart */}
            {pesoData.length > 0 && (
              <ProgressChart
                data={pesoData}
                dataKey="peso"
                title="Evolução do Peso"
                color="#B8977A"
              />
            )}

            {/* Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                data-testid="create-prescription-button"
                onClick={() => setShowPrescriptionForm(!showPrescriptionForm)}
                className="bg-[#0F2B4C] text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
              >
                <FileText size={32} className="mb-3" />
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Nova Prescrição
                </h3>
                <p className="text-sm opacity-90">Cadastrar receita médica</p>
              </button>

              <button
                data-testid="create-mealplan-button"
                onClick={() => setShowMealPlanForm(!showMealPlanForm)}
                className="bg-[#B8977A] text-white rounded-2xl p-6 text-left hover:shadow-lg transition-shadow"
              >
                <UtensilsCrossed size={32} className="mb-3" />
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Novo Plano Alimentar
                </h3>
                <p className="text-sm opacity-90">Criar plano personalizado</p>
              </button>
            </div>
          </>
        )}

        {/* Prescription Form */}
        {showPrescriptionForm && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nova Prescrição
            </h3>
            <form onSubmit={handleCreatePrescription} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Medicamento</label>
                <input
                  data-testid="input-medicamento"
                  type="text"
                  value={prescriptionData.medicamento}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, medicamento: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Dosagem</label>
                <input
                  data-testid="input-dosagem"
                  type="text"
                  value={prescriptionData.dosagem}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, dosagem: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  placeholder="10mg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Horários (separados por vírgula)</label>
                <input
                  data-testid="input-horarios"
                  type="text"
                  value={prescriptionData.horarios}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, horarios: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  placeholder="08:00, 20:00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Duração (dias)</label>
                <input
                  data-testid="input-duracao"
                  type="number"
                  value={prescriptionData.duracao_dias}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, duracao_dias: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Observações</label>
                <textarea
                  data-testid="input-observacoes"
                  value={prescriptionData.observacoes}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, observacoes: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl p-4"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">PDF da Receita (opcional)</label>
                <div className="flex items-center gap-4">
                  <label className="flex-1 cursor-pointer">
                    <div className="bg-muted rounded-xl p-4 border-2 border-dashed border-[#B8977A]/30 hover:border-[#B8977A] transition-colors text-center">
                      {pdfFileName ? (
                        <div className="flex items-center justify-center gap-2 text-[#B8977A]">
                          <FileText size={20} />
                          <span className="font-medium">{pdfFileName}</span>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          <FileText size={24} className="mx-auto mb-2" />
                          <span>Clique para anexar PDF</span>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfChange}
                      className="hidden"
                      data-testid="input-pdf"
                    />
                  </label>
                  {pdfFileName && (
                    <button
                      type="button"
                      onClick={() => {
                        setPrescriptionData({ ...prescriptionData, pdf_receita: '' });
                        setPdfFileName('');
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="submit-prescription"
                  type="submit"
                  className="flex-1 bg-[#0F2B4C] text-white rounded-full py-3 font-bold"
                >
                  Criar Prescrição
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrescriptionForm(false)}
                  className="flex-1 bg-muted text-muted-foreground rounded-full py-3 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Meal Plan Form */}
        {showMealPlanForm && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Novo Plano Alimentar
            </h3>
            <form onSubmit={handleCreateMealPlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Orientações Gerais</label>
                <textarea
                  data-testid="input-orientacoes"
                  value={mealPlanData.orientacoes}
                  onChange={(e) => setMealPlanData({ ...mealPlanData, orientacoes: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl p-4"
                  rows={3}
                  placeholder="Ex: Beber 2L de água por dia, evitar frituras..."
                />
              </div>

              {mealPlanData.refeicoes.map((refeicao, index) => (
                <div key={index} className="bg-muted rounded-xl p-4">
                  <h4 className="font-semibold mb-3 text-highlight">{refeicao.nome} - {refeicao.horario}</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">Alimentos (um por linha)</label>
                      <textarea
                        data-testid={`input-alimentos-${index}`}
                        value={refeicao.alimentos.join('\n')}
                        onChange={(e) => updateMealAlimentos(index, e.target.value)}
                        className="w-full bg-background border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl p-4"
                        rows={4}
                        placeholder="Ex:&#10;2 ovos mexidos&#10;1 fatia de pão integral&#10;1 copo de suco natural"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Calorias (opcional)</label>
                      <input
                        data-testid={`input-calorias-${index}`}
                        type="number"
                        value={refeicao.calorias}
                        onChange={(e) => {
                          const newRefeicoes = [...mealPlanData.refeicoes];
                          newRefeicoes[index].calorias = e.target.value;
                          setMealPlanData({ ...mealPlanData, refeicoes: newRefeicoes });
                        }}
                        className="w-full bg-background border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                        placeholder="Ex: 350"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-3">
                <button
                  data-testid="submit-mealplan"
                  type="submit"
                  className="flex-1 bg-[#B8977A] text-white rounded-full py-3 font-bold"
                >
                  Criar Plano Alimentar
                </button>
                <button
                  type="button"
                  onClick={() => setShowMealPlanForm(false)}
                  className="flex-1 bg-muted text-muted-foreground rounded-full py-3 font-bold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Existing Prescriptions */}
        {patientData.prescriptions && patientData.prescriptions.length > 0 && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Prescrições Cadastradas
            </h3>
            <div className="space-y-3">
              {patientData.prescriptions.slice(0, 5).map((presc) => (
                <div key={presc.id} className="p-4 bg-muted rounded-xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-highlight">{presc.medicamento}</p>
                      <p className="text-sm text-muted-foreground">{presc.dosagem} - {presc.horarios.join(', ')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {presc.pdf_receita && (
                        <span className="px-3 py-1 bg-[#0F2B4C]/10 text-[#0F2B4C] rounded-full text-xs font-medium flex items-center gap-1">
                          <FileText size={12} />
                          PDF
                        </span>
                      )}
                      {presc.ativa && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Ativa
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Existing Meal Plans */}
        {patientData.meal_plans && patientData.meal_plans.length > 0 && (
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <h3 className="text-lg font-semibold mb-4 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Planos Alimentares
            </h3>
            <div className="space-y-3">
              {patientData.meal_plans.slice(0, 3).map((plan) => (
                <div key={plan.id} className="p-4 bg-muted rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">
                    Criado em: {formatDateBR(plan.data_criacao)}
                  </p>
                  <p className="text-sm">{plan.orientacoes}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPacienteDetalhes;
